import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #collectionUiFrame = 0;

  #resultsObserver;

  #lastCardCount = 0;

  #lastResultsLabel = '';

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    window.addEventListener('resize', this.#scheduleCollectionUiSync);
    if (this.hasAttribute('data-sp-collection') || this.hasAttribute('data-sp-search')) {
      this.#lastCardCount = Array.isArray(this.refs.cards) ? this.refs.cards.length : 0;
      this.#lastResultsLabel = this.#getResultsLabel();
      this.#updateQuickPresetLinks();
      this.#observeResults();
      this.#scheduleCollectionUiSync();
      if (document.fonts) document.fonts.ready.then(this.#scheduleCollectionUiSync);
    }
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('resize', this.#scheduleCollectionUiSync);
    this.#resultsObserver?.disconnect();
    cancelAnimationFrame(this.#collectionUiFrame);
  }

  #observeResults() {
    this.#resultsObserver = new MutationObserver((mutations) => {
      const selector = '[ref="grid"], [ref="cards[]"], .sp-collection-filter-chips';
      const hasRelevantChange = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) =>
            node instanceof Element && (node.matches(selector) || node.querySelector(selector))
        )
      );

      if (hasRelevantChange) this.#scheduleCollectionUiSync();
    });
    this.#resultsObserver.observe(this, { childList: true, subtree: true });
  }

  #scheduleCollectionUiSync = () => {
    if (!this.isConnected) return;

    cancelAnimationFrame(this.#collectionUiFrame);
    this.#collectionUiFrame = requestAnimationFrame(() => {
      this.#updateQuickPresetLinks();
      this.#alignProductCardRails();
      this.#announceResultsChange();
    });
  };

  #getResultsLabel() {
    return this.querySelector('.products-count-wrapper span')?.textContent?.trim() ?? '';
  }

  #announceResultsChange() {
    const status = this.querySelector('[ref="resultsStatus"]');
    if (!(status instanceof HTMLElement)) return;

    const cardCount = Array.isArray(this.refs.cards) ? this.refs.cards.length : 0;
    const resultsLabel = this.#getResultsLabel();
    let message = '';

    if (cardCount > this.#lastCardCount) {
      const loaded = cardCount - this.#lastCardCount;
      message = (status.dataset.itemsLoadedTemplate || '__COUNT__ products loaded').replace('__COUNT__', String(loaded));
    } else if (resultsLabel && resultsLabel !== this.#lastResultsLabel) {
      message = resultsLabel;
    }

    this.#lastCardCount = cardCount;
    this.#lastResultsLabel = resultsLabel;
    if (!message) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      if (status.isConnected) status.textContent = message;
    });
  }

  #alignProductCardRails = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const titles = [...grid.querySelectorAll('.sp-product-title')];
    if (!titles.length) return;

    const columnCount = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    if (columnCount < 2) {
      grid.style.removeProperty('--sp-product-title-rail-height');
      return;
    }

    const tallestTitle = Math.max(
      ...titles.map((title) => {
        const text = title.querySelector('.text-block, p');
        const style = getComputedStyle(title);
        const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        return Math.ceil((text?.scrollHeight ?? title.scrollHeight) + padding);
      })
    );
    grid.style.setProperty('--sp-product-title-rail-height', `${tallestTitle}px`);
  };

  /**
   * Builds native Shopify filter/sort URLs while preserving unrelated active
   * filters and marks the matching quick preset as selected.
   */
  #updateQuickPresetLinks() {
    const links = this.querySelectorAll('[data-sp-quick-preset]');
    if (!links.length) return;

    const current = new URL(window.location.href);
    const currentParams = current.searchParams;

    links.forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const preset = link.dataset.spQuickPreset;
      const url = new URL(current.href);
      const params = url.searchParams;
      params.delete('page');

      let isActive = false;

      if (preset === 'all') {
        isActive = ![...currentParams.keys()].some((key) => key.startsWith('filter.')) && !currentParams.has('sort_by');
        [...params.keys()].forEach((key) => {
          if (key.startsWith('filter.')) params.delete(key);
        });
        params.delete('sort_by');
      } else if (preset === 'in-stock') {
        isActive = currentParams.get('filter.v.availability') === '1';
        if (isActive) params.delete('filter.v.availability');
        else params.set('filter.v.availability', '1');
      } else if (preset === 'under-100') {
        isActive = !currentParams.has('filter.v.price.gte') && currentParams.get('filter.v.price.lte') === '99.99';
        params.delete('filter.v.price.gte');
        if (isActive) params.delete('filter.v.price.lte');
        else params.set('filter.v.price.lte', '99.99');
      } else if (preset === '100-200') {
        isActive = currentParams.get('filter.v.price.gte') === '100' && currentParams.get('filter.v.price.lte') === '200';
        if (isActive) {
          params.delete('filter.v.price.gte');
          params.delete('filter.v.price.lte');
        } else {
          params.set('filter.v.price.gte', '100');
          params.set('filter.v.price.lte', '200');
        }
      } else if (preset === 'newest') {
        isActive = currentParams.get('sort_by') === 'created-descending';
        if (isActive) params.delete('sort_by');
        else params.set('sort_by', 'created-descending');
      } else if (preset === 'best-selling') {
        isActive = currentParams.get('sort_by') === 'best-selling';
        if (isActive) params.delete('sort_by');
        else params.set('sort_by', 'best-selling');
      }

      link.href = `${url.pathname}${url.search}${url.hash}`;
      link.classList.toggle('is-active', isActive);
      const visibleLabel = link.textContent.trim();
      const selectedLabel = link.dataset.selectedLabel || 'Selected';
      link.setAttribute('aria-label', isActive ? `${visibleLabel} — ${selectedLabel}` : visibleLabel);
      link.removeAttribute('aria-current');
      link.removeAttribute('aria-pressed');
    });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Mobile single/double layouts change card geometry dramatically. Native view
    // transitions can preserve the single-column snapshot height when returning
    // to two columns, leaving a large blank area inside each card on some browsers.
    if (mediaQueryLarge.matches) {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } else {
      this.#setLayout(value);
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    if (this.hasAttribute('data-sp-collection') || this.hasAttribute('data-sp-search')) this.#scheduleCollectionUiSync();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
